<template>
  <div>

    <Head :title="`Manage Group: ${group.name}`" />

    <!-- Navigation -->
    <div class="flex items-center justify-between mb-6">
      <Link class="btn-gray" :href="`/accessory-groups/${group.id}`">
      ← Back to Group
      </Link>
    </div>

    <!-- Page Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold">Manage {{ group.name }} Accessories</h1>
      <p class="text-gray-600 mt-2">Add accessories and configure their tooltips and formulas</p>
    </div>
    <!-- Group Accessories -->
    <div class="bg-white shadow sm:rounded-lg">
      <div class="flex justify-between px-4 py-5 sm:px-6 border-b border-gray-200">
        <div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">
            Group Accessories
          </h3>
          <p class="mt-1 text-sm text-gray-500">
            {{ groupAccessories.length }} accessories in this group
          </p>
        </div>

        <div class="flex gap-4">
          <button @click="showAddExistingModal = true"
            class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6">
              </path>
            </svg>
            Add Existing Accessories
          </button>
          <Link href="/accessories/create"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6">
              </path>
            </svg>
            Create New Accessory
          </Link>
        </div>
      </div>

      <div class="overflow-y-auto">
        <div v-if="groupAccessories.length === 0" class="p-6 text-center text-gray-500">
          No accessories in this group yet
        </div>
        <div v-else class="divide-y divide-gray-200">
          <div v-for="accessory in groupAccessories" :key="`group-${accessory.id}`" class="p-4">
            <div class="flex items-start justify-between">
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-3">
                  <div class="text-lg font-medium text-gray-900">
                    {{ accessory.part_number }}
                  </div>
                  <button @click="removeAccessory(accessory)" class="text-red-600 hover:text-red-800 p-1 rounded"
                    title="Remove from group">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                      </path>
                    </svg>
                  </button>
                </div>

                <div class="text-sm text-gray-600 mb-3">
                  {{ accessory.description || 'No description' }}
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Tooltip Input -->
                  <div>
                    <label class="text-sm font-medium text-gray-700 block mb-1">Tooltip</label>
                    <textarea v-model="accessory.tooltip"
                      class="w-full text-sm border border-gray-300 rounded-md p-2 resize-none" rows="1"
                      placeholder="Enter tooltip text..." @blur="updateAccessory(accessory)" />
                  </div>

                  <!-- Formula Input -->
                  <div>
                    <label class="text-sm font-medium text-gray-700 block mb-1">Calculation Formula</label>
                    <input v-model="accessory.calculation_formula" type="text"
                      class="w-full text-sm border border-gray-300 rounded-md p-2"
                      placeholder="e.g., channels * 2, Math.ceil(length)" @blur="updateAccessory(accessory)" />

                    <!-- Optional Checkbox -->
                    <!-- <div class="flex items-center mt-2">
                      <input :id="`optional-${accessory.id}`" v-model="accessory.is_optional" type="checkbox"
                        class="h-4 w-4 text-blue-600 border-gray-300 rounded" @change="updateAccessory(accessory)" />
                      <label :for="`optional-${accessory.id}`" class="ml-2 text-sm font-medium text-gray-700">
                        Optional accessory
                      </label>
                    </div> -->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Existing Accessories Modal -->
    <div v-if="showAddExistingModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="closeAddExistingModal">
      <div class="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-medium text-gray-900">Add Existing Accessories</h3>
            <button @click="showAddExistingModal = false" class="text-gray-400 hover:text-gray-600">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div v-if="availableAccessories.length > 0">
            <div class="mb-4">
              <input v-model="searchQuery" type="text" placeholder="Search accessories..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
            </div>

            <div class="max-h-96 overflow-y-auto">
              <div class="grid grid-cols-1 gap-3">
                <div v-for="accessory in filteredAvailableAccessories" :key="`modal-available-${accessory.id}`"
                  class="flex items-center justify-between p-4 border border-gray-200 rounded-md hover:bg-gray-50">
                  <div class="flex-1">
                    <div class="text-sm font-medium text-gray-900">
                      {{ accessory.part_number }}
                    </div>
                    <div class="text-xs text-gray-500 mt-1">
                      {{ accessory.description || 'No description' }}
                    </div>
                    <div v-if="accessory.tooltip" class="text-xs text-blue-600 mt-1">
                      {{ accessory.tooltip }}
                    </div>
                  </div>
                  <button @click="addExistingAccessory(accessory)"
                    class="ml-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium">
                    Add to Group
                  </button>
                </div>
              </div>
            </div>

            <div v-if="filteredAvailableAccessories.length === 0 && searchQuery" class="text-center text-gray-500 py-8">
              No accessories found matching "{{ searchQuery }}"
            </div>
          </div>

          <div v-else class="text-center text-gray-500 py-8">
            <div class="text-lg">All accessories are already in this group</div>
            <div class="text-sm mt-2">Create a new accessory instead</div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'

export default {
  components: {
    Head,
    Link,
    TextInput,
  },
  layout: Layout,
  props: {
    group: Object,
    groupAccessories: Array,
    availableAccessories: Array,
    systems: Array,
    widths: Array,
    edges: Array,
    outletTypes: Array,
  },
  data() {
    return {
      groupAccessories: [...this.groupAccessories],
      availableAccessories: [...this.availableAccessories],
      showAddExistingModal: false,
      searchQuery: '',
    }
  },
  computed: {
    filteredAvailableAccessories() {
      if (!this.searchQuery) {
        return this.availableAccessories
      }
      
      const query = this.searchQuery.toLowerCase()
      return this.availableAccessories.filter(accessory => 
        accessory.part_number.toLowerCase().includes(query) ||
        (accessory.description && accessory.description.toLowerCase().includes(query)) ||
        (accessory.tooltip && accessory.tooltip.toLowerCase().includes(query))
      )
    }
  },
  methods: {
    updateAccessory(accessory) {
      // Send API request to update accessory
      this.$inertia.put(`/accessory-groups/${this.group.id}/accessories/${accessory.id}`, {
        tooltip: accessory.tooltip,
        calculation_formula: accessory.calculation_formula,
        is_optional: accessory.is_optional,
      }, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          // Update local data
          const index = this.groupAccessories.findIndex(a => a.id === accessory.id)
          if (index !== -1) {
            this.groupAccessories[index] = { ...accessory }
          }
        }
      })
    },

    addExistingAccessory(accessory) {
      // Send API request to add existing accessory to group
      this.$inertia.post(`/accessory-groups/${this.group.id}/accessories`, {
        accessory_id: accessory.id
      }, {
        preserveScroll: true,
        preserveState: false,
        onSuccess: () => {
          // Move accessory from available to group locally
          this.groupAccessories.push(accessory)
          this.availableAccessories = this.availableAccessories.filter(a => a.id !== accessory.id)
          
          // Close modal and reset search
          this.showAddExistingModal = false
          this.searchQuery = ''
        }
      })
    },

    removeAccessory(accessory) {
      if (confirm(`Are you sure you want to remove ${accessory.part_number} from this group?`)) {
        this.$inertia.delete(`/accessory-groups/${this.group.id}/accessories`, {
          data: { accessory_id: accessory.id },
          preserveScroll: true,
          preserveState: false,
          onSuccess: () => {
            // Move accessory from group back to available locally
            this.groupAccessories = this.groupAccessories.filter(a => a.id !== accessory.id)
            this.availableAccessories.push(accessory)
            // Sort available accessories by part_number
            this.availableAccessories.sort((a, b) => a.part_number.localeCompare(b.part_number))
          }
        })
      }
    },


    closeAddExistingModal(event) {
      if (event.target === event.currentTarget) {
        this.showAddExistingModal = false
        this.searchQuery = ''
      }
    },

  },
}
</script>